import type { ReactNode } from 'react';
import type {
  NamedView,
  SurveyFeatures,
  SurveySpread,
  TerrainProfileView,
  TerrainSummary,
} from '../api/catalog';
import type { RecipeView } from '../api/missions';
import { ca } from '../i18n/ca';
import { TerrainImage } from './TerrainImage';
import { surfaceLabel } from './terrainSurfaces';

const num = (n: number) => n.toLocaleString('ca');
const km = (n: number) => n.toLocaleString('ca', { maximumFractionDigits: 1 });

/** One row of a card: a label and its value; a row whose value is null is not printed. */
type Row = [label: string, value: ReactNode | null, sub?: string];

function Rows({ rows }: { rows: Row[] }) {
  const shown = rows.filter(([, value]) => value !== null && value !== undefined);
  if (shown.length === 0) return null;
  return (
    <dl className="sheet-rows">
      {shown.map(([label, value, sub]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>
            {value}
            {sub && <span className="sub"> {sub}</span>}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function Card({
  icon,
  title,
  wide,
  children,
}: {
  icon: string;
  title: string;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={`sheet-card${wide ? ' wide' : ''}`}>
      <h3 className="sheet-card-head">
        <i className={`bi bi-${icon}`} aria-hidden="true" /> {title}
      </h3>
      {children}
    </div>
  );
}

/** Bars of counts, the longest first, scaled to the first. */
function Bars({
  rows,
}: {
  rows: { label: string; value: number; text: string; title?: string }[];
}) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <ul className="sheet-bars">
      {rows.map((r) => (
        <li key={r.label} title={r.title}>
          <span className="name">{r.label}</span>
          <span className="track" aria-hidden="true">
            <span className="fill" style={{ width: `${(r.value / max) * 100}%` }} />
          </span>
          <span className="n">{r.text}</span>
        </li>
      ))}
    </ul>
  );
}

/** The same kinds of ground summed under one word, most first. */
function surfaceBars(surfaces: { kind: string; percent: number }[]) {
  const sums = new Map<string, { value: number; raw: string[] }>();
  for (const s of surfaces) {
    const { label, raw } = surfaceLabel(s.kind);
    const row = sums.get(label) ?? { value: 0, raw: [] };
    row.value += s.percent;
    if (raw) row.raw.push(raw);
    sums.set(label, row);
  }
  return [...sums.entries()]
    .sort((a, b) => b[1].value - a[1].value)
    .map(([label, row]) => ({
      label,
      value: row.value,
      text: `${km(row.value)} %`,
      title: row.raw.length ? row.raw.join(', ') : undefined,
    }));
}

const spread = (s: SurveySpread | undefined) =>
  s && s.pieces > 0 ? ca.terrainPage.sheet.spread(num(s.pieces), num(s.hectares)) : null;

/**
 * The terrain sheet (the owner's choice of 2026-10-18, layout A of the review
 * page): the picture and the key figures, then one card a topic with every
 * figure the last completed survey and the catalogue know. A figure nobody
 * measured is left out, never printed as a zero or a cap; the layered map and
 * the tables follow it on the page.
 */
export function TerrainSheet({
  world,
  title,
  profile,
  summary,
  features,
  named,
  recipes,
  kinds,
  meta,
}: {
  world: string;
  title: string;
  profile: (TerrainProfileView & { junctions?: number }) | null;
  summary: TerrainSummary | null;
  features: SurveyFeatures | null;
  named: NamedView | null;
  /** The recipes judged against this terrain with its mod set, or null when not known. */
  recipes: RecipeView[] | null;
  /** The kinds of ground the terrain offers (`airfield`, `water`...), or null when not known. */
  kinds: string[] | null;
  /** The survey's own line: when, which version, the sample step. */
  meta: ReactNode;
}) {
  const t = ca.terrainPage;
  const s = t.sheet;
  const places = named?.places ?? [];
  const settlements = places.filter((p) => p.settlement).length;
  const ofType = (...types: string[]) => places.filter((p) => types.includes(p.type));
  const capitals = ofType('NameCityCapital');
  const towns = ofType('NameCityCapital', 'NameCity', 'NameVillage').length;
  const sites = features?.sites ?? [];
  const ground = summary?.ground ?? null;
  const hasData = profile != null && (profile.cols != null || profile.status === 'DONE');
  const total = (key: string) => summary?.lists[key]?.total ?? null;
  const runways = summary?.runways ?? [];
  const bridges = summary?.bridgeRecords ?? [];
  const playable = (recipes ?? []).filter((r) => r.available && !r.planned);
  const fit = playable.filter((r) => r.compatible !== false);
  const out = playable.filter((r) => r.compatible === false);

  const key: { value: string; label: string }[] = [
    ...(summary?.landKm2 != null
      ? [{ value: `${km(summary.landKm2)} km²`, label: `${s.key.land} · ${summary.landPercent} %` }]
      : []),
    ...(summary?.highest != null
      ? [{ value: t.metres(num(summary.highest)), label: s.key.highest }]
      : []),
    ...(named ? [{ value: num(towns), label: s.key.towns }] : []),
    ...(summary?.buildings != null
      ? [{ value: num(summary.buildings), label: s.key.buildings }]
      : []),
    ...(runways.length || named?.airfields.length
      ? [{ value: String(runways.length || named!.airfields.length), label: s.key.runways }]
      : []),
    ...(bridges.length || hasData
      ? [{ value: num(bridges.length || features?.bridges?.length || 0), label: s.key.bridges }]
      : []),
    ...(hasData ? [{ value: num(sites.length), label: s.key.sites }] : []),
    ...(playable.length
      ? [{ value: `${fit.length}/${playable.length}`, label: s.key.missions }]
      : []),
  ];

  const types = summary?.buildingTypes ?? [];
  const shore =
    ground && ground.shorePoints > 0
      ? Math.round((100 * ground.landableShore) / ground.shorePoints)
      : null;
  // The road net as one figure, the footpaths apart: what a column or a patrol has to drive on.
  const roadTotal = ground
    ? Object.entries(ground.roadKm)
        .filter(([type]) => type !== 'footpath')
        .reduce((sum, [, value]) => sum + value, 0)
    : null;

  return (
    <section className="terrain-sheet" aria-label={title}>
      <div className="sheet-top">
        <TerrainImage
          world={world}
          label={title}
          size="card"
          className="sheet-picture"
          alt={title}
        />
        <div className="sheet-head">
          <div className="chips">
            {hasData &&
              profile?.surveyVersion != null &&
              profile.surveyVersion >= (profile.currentSurveyVersion ?? 0) && (
                <span className="chip ok">
                  <i className="bi bi-check2-circle" aria-hidden="true" />{' '}
                  {s.current(profile.surveyVersion)}
                </span>
              )}
          </div>
          {key.length > 0 && (
            <ul className="sheet-key" aria-label={s.keyFigures}>
              {key.map((k) => (
                <li key={k.label}>
                  <span className="v">{k.value}</span>
                  <span className="l">{k.label}</span>
                </li>
              ))}
            </ul>
          )}
          {meta}
        </div>
      </div>

      <div className="sheet-grid" aria-label={t.figuresLabel}>
        <Card icon="triangle" title={s.cards.relief}>
          <Rows
            rows={[
              [t.figures.highest, summary?.highest != null ? t.metres(num(summary.highest)) : null],
              [t.figures.mean, summary?.mean != null ? t.metres(num(summary.mean)) : null],
              [s.lowest, summary?.lowest != null ? t.metres(num(summary.lowest)) : null],
              [t.layers.highGround, total('highGround') != null ? num(total('highGround')!) : null],
              [t.layers.flatAreas, total('flatAreas') != null ? num(total('flatAreas')!) : null],
              [s.namedHills, named ? num(ofType('Hill', 'Mount').length) : null],
            ]}
          />
        </Card>

        <Card icon="water" title={s.cards.water}>
          <Rows
            rows={[
              [
                t.figures.land,
                summary?.landKm2 != null && summary.landPercent != null
                  ? t.landValue(num(summary.landKm2), summary.landPercent)
                  : null,
              ],
              [s.water, summary?.waterKm2 != null ? t.km2(num(summary.waterKm2)) : null],
              [t.landMasses, summary?.landMasses != null ? num(summary.landMasses) : null],
              [t.layers.beaches, total('beaches') != null ? num(total('beaches')!) : null],
              [t.layers.lakes, total('lakes') != null ? num(total('lakes')!) : null],
              [
                s.landable,
                shore != null ? s.landableValue(shore) : null,
                ground && shore != null
                  ? s.shorePoints(num(ground.landableShore), num(ground.shorePoints))
                  : undefined,
              ],
              [s.marine, named ? num(ofType('NameMarine').length) : null],
            ]}
          />
        </Card>

        <Card icon="house" title={s.cards.places}>
          <Rows
            rows={[
              [t.layers.settlements, named ? String(settlements) : null],
              [
                s.capitals,
                named ? String(capitals.length) : null,
                capitals
                  .map((p) => p.name)
                  .filter(Boolean)
                  .join(', ') || undefined,
              ],
              [s.cities, named ? num(ofType('NameCity').length) : null],
              [s.villages, named ? num(ofType('NameVillage').length) : null],
              [s.localities, named ? num(ofType('NameLocal').length) : null],
              [t.layers.places, named ? String(places.length - settlements) : null],
              [
                s.landmarks,
                summary?.landmarks != null ? num(summary.landmarks) : null,
                s.landmarksHint,
              ],
            ]}
          />
        </Card>

        <Card icon="signpost-split" title={s.cards.roads}>
          <Rows
            rows={[
              [s.roadTotal, roadTotal != null && roadTotal > 0 ? s.km(km(roadTotal)) : null],
              ...Object.entries(ground?.roadKm ?? {}).map(([type, value]): Row => [
                s.roadTypes[type] ?? s.roadTypes.ROAD,
                s.km(km(value)),
              ]),
              [s.junctions, profile?.junctions ? num(profile.junctions) : null],
              [s.deadEnds, ground ? num(ground.deadEnds) : null],
              [s.fords, ground ? num(ground.fords) : null],
              [
                t.bridgesTitle,
                bridges.length
                  ? s.bridgesOver(bridges.length, bridges.filter((b) => b.overWater).length)
                  : null,
              ],
              // Malden's bridges carry no span: the row says nobody measured it rather than vanish.
              [
                s.longestBridge,
                summary?.longestBridge != null
                  ? t.metres(num(summary.longestBridge))
                  : bridges.length
                    ? '—'
                    : null,
                summary?.longestBridge == null ? s.unmeasured : undefined,
              ],
            ]}
          />
        </Card>

        <Card icon="airplane" title={s.cards.air}>
          <Rows
            rows={[
              [t.figures.runways, runways.length ? String(runways.length) : null],
              ...runways.map((r): Row => [
                `${t.runwayNames[r.airport] ?? (r.name || '—')}${r.airport === 'secondary' && r.index > 0 ? ` ${r.index}` : ''}`,
                r.length > 0 ? t.metres(num(r.length)) : '—',
                `${r.heading}° · ${r.paved ? t.paved : t.unpaved}`.toLowerCase(),
              ]),
              [s.helipads, ground ? num(ground.helipads) : null],
            ]}
          />
        </Card>

        <Card icon="flag" title={s.cards.military}>
          {hasData && sites.length === 0 ? (
            <p className="muted small">{s.noSites}</p>
          ) : (
            <Rows
              rows={[
                [t.layers.sites, hasData ? num(sites.length) : null],
                ...[...sites]
                  .sort((a, b) => (b.count ?? 0) - (a.count ?? 0))
                  .slice(0, 3)
                  .map((site, i): Row => [
                    s.biggestSite[i],
                    s.siteValue(site.count ?? 0, site.radius ?? 0),
                    Object.entries(site.kinds ?? {})
                      .sort((a, b) => b[1] - a[1])
                      .map(([kind, n]) => `${t.siteKinds[kind] ?? kind} ${n}`)
                      .join(' · ') || undefined,
                  ]),
              ]}
            />
          )}
        </Card>

        <Card icon="buildings" title={s.cards.buildings} wide>
          <Rows
            rows={[
              [
                t.figures.buildings,
                summary?.buildings != null ? num(summary.buildings) : null,
                profile?.buildingClasses ? s.models(num(profile.buildingClasses)) : undefined,
              ],
              [
                t.figures.transmitters,
                summary?.transmitters != null ? num(summary.transmitters) : null,
              ],
              [s.ruins, summary?.ruinPlaces != null ? num(summary.ruinPlaces) : null, s.ruinsHint],
            ]}
          />
          {types.length > 0 && (
            <Bars
              rows={types.slice(0, 12).map((ty) => ({
                label: s.types[ty.type] ?? s.otherType,
                value: ty.objects,
                text: num(ty.objects),
                title: s.types[ty.type] ? undefined : ty.type,
              }))}
            />
          )}
        </Card>

        <Card icon="puzzle" title={s.cards.missions}>
          {playable.length > 0 && (
            <p className="sheet-big">
              <span className="v">{s.missionsFit(fit.length, playable.length)}</span>{' '}
              <span className="l">{s.missionsHint}</span>
            </p>
          )}
          {kinds && kinds.length > 0 && (
            <>
              <p className="muted small">{s.offers}</p>
              <div className="chips">
                {kinds.map((k) => (
                  <span className="chip" key={k}>
                    {s.kinds[k] ?? k}
                  </span>
                ))}
              </div>
            </>
          )}
          {playable.length > 0 &&
            (out.length === 0 ? (
              <p className="muted small">{s.missionsAll}</p>
            ) : (
              <>
                <p className="muted small">{s.missionsOut}</p>
                <ul className="sheet-out">
                  {out.map((r) => (
                    <li key={r.id}>
                      <i className={`bi bi-${r.icon}`} aria-hidden="true" /> {r.title}
                      {r.reasons?.[0] && <span className="sub"> · {r.reasons[0].reason}</span>}
                    </li>
                  ))}
                </ul>
              </>
            ))}
        </Card>

        {ground && (
          <Card icon="bricks" title={s.cards.objects}>
            <Rows
              rows={[
                [s.lamps, num(ground.lamps)],
                [s.walls, spread(ground.walls)],
                [s.fences, spread(ground.fences)],
                [s.rocks, spread(ground.rocks)],
                [s.powerLines, spread(ground.powerLines)],
                ...Object.entries(ground.minor)
                  .filter(([, v]) => v.pieces > 0)
                  .map(([name, v]): Row => [s.minor[name] ?? s.otherType, num(v.pieces)]),
              ]}
            />
          </Card>
        )}

        {ground && ground.surfaces.length > 0 && (
          <Card icon="tree" title={s.cards.ground}>
            <p className="muted small">{s.surfacesHint}</p>
            <Bars rows={surfaceBars(ground.surfaces)} />
          </Card>
        )}
      </div>
    </section>
  );
}
